import styles from './deliveryOrdersDataList.module.css'
import { useMemo } from 'react'
import { addressHelpers } from '../../../../../features/address'
import { Labels, useDataTypesHooks } from '../../../../../features/data-types'
import { Button, FieldList, FieldType, FormField } from '../../../../../features/ui'
import { Customer } from '../../../../sales'
import { Delivery, DeliveryData, DeliveryMethod, DeliveryOrder } from '../../../state/types'
import { DeliveryOrderDataList } from '../delivery-order-data-list/deliveryOrderDataList'
import { toDate } from '../../../../../utils/typeUtils'
import { RoutePath } from '../../../../../features/routes'
import { useNavigate } from 'react-router-dom'
import { useBranchLoaders } from '../../../../manager'
import { DeliveryDataList } from '../../delivery/delivery-data-list/deliveryDataList'
import { toList } from '../../../../../utils/listUtils'
import { Operation } from '../../../../operations'

type Props = {
    deliveryData?: DeliveryData
    deliveryOrders: DeliveryOrder[]
    deliveries: Delivery[]
    operation: Operation
    customer?: Customer
    branchId?: string
    disableEdit?: boolean
    readOnly?: boolean
    // The delivery notes without an order are shown in the documents tab instead
    hideDeliveries?: boolean
    onChangeCurrentDelivery: (delivery: Delivery) => void
    onRefresh?: () => void
}

export const DeliveryOrdersDataList = (props: Props) => {
    const { deliveryData, deliveryOrders, deliveries, operation, customer, branchId, disableEdit = false, readOnly = false, hideDeliveries = false, onChangeCurrentDelivery, onRefresh } = props

    const { describeAddress } = addressHelpers
    const { formatDate } = useDataTypesHooks()
    const navigate = useNavigate()

    const branchLoaders = useBranchLoaders()

    const address = useMemo(() => deliveryData?.method === DeliveryMethod.SHIPMENT ?
        customer?.deliveryAddresses.find(address => address.id === deliveryData?.addressId) :
        branchLoaders().findBranch(branchId)!.address
    , [customer, deliveryData])

    const pendingQuantity = useMemo(() => operation.items.reduce((acc, item) => acc + (item.deliveryStats?.pending || 0), 0), [operation.items])

    const onEdit = () => navigate(RoutePath.EDIT_DELIVERY_DATA)

    const individualDeliveries = useMemo(() => deliveries.filter(delivery => !delivery.orderId), [deliveries])

    const fields: FormField[] = useMemo(() => {
        const fieldsList: FormField[] = [
            {
                name: 'deliveryData',
                type: FieldType.TEXT,
                label: 'Datos de Entrega',
                labelSpace: 2,
                render: () => (
                    <div className={styles.deliveryData}>
                        {deliveryData ? (<>
                            {Labels.deliveryMethodLabel(deliveryData.method)}
                            {address && " - " + describeAddress(address)}
                            {deliveryData.deliveryDate && " - " + formatDate(toDate(deliveryData.deliveryDate))}
                        </>) : (
                            <>No hay datos de entrega definidos.</>
                        )}
                        {!disableEdit && pendingQuantity > 0 && (
                            <Button
                                label="Editar"
                                icon="pencil-fill"
                                small
                                onClick={onEdit}
                            />
                        )}
                    </div>
                )
            }
        ]
        if (deliveryOrders.length > 0) {
            fieldsList.push({
                name: 'deliveryOrdersData',
                type: FieldType.TABLE,
                render: () => (
                    <DeliveryOrderDataList
                        deliveryOrders={deliveryOrders}
                        deliveries={deliveries}
                        operation={operation}
                        readOnly={readOnly}
                        onChangeCurrentDelivery={onChangeCurrentDelivery}
                        onRefresh={onRefresh}
                    />
                )
            })
        }
        if (!hideDeliveries && individualDeliveries.length > 0) {
            fieldsList.push({
                name: 'individualDeliveries',
                type: FieldType.TABLE,
                render: () => (
                    <DeliveryDataList
                        deliveries={individualDeliveries}
                        operations={toList(operation)}
                        defaultExpanded={false}
                        onChangeCurrentDelivery={onChangeCurrentDelivery}
                        onRefresh={onRefresh}
                    />
                )
            })
        }
        return fieldsList
    }, [deliveryData, address, pendingQuantity, deliveryOrders, deliveries, operation, readOnly, hideDeliveries, onRefresh])

    return (
        <FieldList fields={fields} />
    )
}
